<!--
  @copyright 汽车商贸管理平台 V1.0
  @year 2026
  @description InventoryRecords 盘点记录 = 已完成台账（订单 → 品牌×数量 → 车架号 + 4类车辆权属材料计数/预览）
-->
<template>
  <div class="records">
    <van-nav-bar title="盘点记录" left-arrow @click-left="$router.back()" />

    <!-- 搜索 -->
    <div class="search-bar">
      <van-search
        v-model="searchText"
        placeholder="搜索订单号/车架号/品牌/公司/盘点人"
        shape="round"
      />
    </div>

    <div class="order-list" v-if="!loading">
      <div v-if="orderGroups.length === 0" class="empty-tip">暂无盘点记录</div>

      <div
        class="order-card"
        v-for="g in orderGroups"
        :key="g.orderNo"
        @click="toggleOrder(g.orderNo)"
      >
        <div class="order-head">
          <span
            class="order-no"
            :class="{ unlinked: g.orderNo === '未关联订单' }"
            >{{ g.orderNo }}</span
          >
          <span class="order-count">共 {{ g.count }} 台</span>
          <span class="order-arrow">
            {{ expandedOrder === g.orderNo ? "收起" : "展开" }}
          </span>
        </div>
        <div class="brand-chips">
          <span class="brand-chip" v-for="(b, i) in g.brands" :key="i">
            {{ b.text }} × {{ b.count }}
          </span>
        </div>

        <!-- 展开：品牌分组 → 车架号（材料计数可点预览） -->
        <div v-if="expandedOrder === g.orderNo" class="order-detail">
          <div v-for="(bg, j) in g.itemGroups" :key="j" class="brand-block">
            <div class="brand-title">{{ bg.text }} × {{ bg.count }}</div>
            <div class="vin-row" v-for="item in bg.items" :key="item.no">
              <div class="vin-main">
                <div class="vin-top">
                  <span class="vin-vid">{{ item.vid }}</span>
                  <span
                    class="state-tag"
                    :class="statusDone(item) ? 'ok' : 'doing'"
                  >
                    {{ statusText(item) }}
                  </span>
                  <span class="del-link" @click.stop="confirmReset(item)"
                    >回待盘点</span
                  >
                </div>
                <div class="vin-meta">
                  {{ item.ownerCompany || "未知公司" }} ·
                  {{ item.storageLocation || "-" }}
                </div>
                <div class="vin-meta" v-if="item.checkTime">
                  {{ item.checker || "-" }} 于 {{ item.checkTime }} 盘点
                </div>
                <div class="vin-meta" v-if="item.remark">备注：{{ item.remark }}</div>

                <!-- 4类材料计数 -->
                <div class="cat-chips" v-if="checkCategories.length > 0">
                  <span
                    class="cat-chip"
                    :class="catServerCount(item, cat) > 0 ? 'done' : 'todo'"
                    v-for="cat in checkCategories"
                    :key="cat.分类ID"
                    @click.stop="previewCategory(item, cat)"
                  >
                    <template v-if="catServerCount(item, cat) > 0">
                      ✓ {{ cat.分类名称 }} {{ catServerCount(item, cat) }}
                    </template>
                    <template v-else>{{ cat.分类名称 }} 未传</template>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getCheckedRecords,
  getCheckCategories,
  getVehicleMaterials,
  getMaterialFileBlob,
  deleteCheckRecord,
} from "../api/index";
import { Toast, ImagePreview, Dialog } from "vant";

export default {
  name: "InventoryRecords",
  data() {
    return {
      searchText: "",
      records: [],
      expandedOrder: "",
      loading: false,
      checkCategories: [],
      previewUrls: [],
    };
  },
  computed: {
    filteredRecords() {
      if (!this.searchText) return this.records;
      const kw = this.searchText.trim().toLowerCase();
      if (!kw) return this.records;
      return this.records.filter((item) =>
        [
          item.orderNo,
          item.vid,
          item.vehicleBand,
          item.vehicleModel,
          item.ownerCompany,
          item.storageLocation,
          item.checker,
        ].some((f) => f && f.toLowerCase().includes(kw))
      );
    },
    orderGroups() {
      const orders = new Map();
      this.filteredRecords.forEach((item) => {
        const key = (item.orderNo || "").trim() || "未关联订单";
        if (!orders.has(key)) orders.set(key, []);
        orders.get(key).push(item);
      });
      const result = [];
      orders.forEach((items, orderNo) => {
        const brandMap = new Map();
        items.forEach((item) => {
          const text =
            ((item.vehicleBand || "") + " " + (item.vehicleModel || "")).trim() ||
            "未知车型";
          if (!brandMap.has(text)) brandMap.set(text, { text, count: 0, items: [] });
          const bg = brandMap.get(text);
          bg.count += 1;
          bg.items.push(item);
        });
        const itemGroups = Array.from(brandMap.values());
        result.push({
          orderNo,
          count: items.length,
          brands: itemGroups.map((b) => ({ text: b.text, count: b.count })),
          itemGroups,
        });
      });
      result.sort((a, b) => {
        if (a.orderNo === "未关联订单") return 1;
        if (b.orderNo === "未关联订单") return -1;
        return b.orderNo.localeCompare(a.orderNo);
      });
      return result;
    },
  },
  created() {
    this.loadCategories();
    this.loadRecords();
  },
  methods: {
    toggleOrder(orderNo) {
      this.expandedOrder = this.expandedOrder === orderNo ? "" : orderNo;
    },
    catServerCount(item, cat) {
      if (!item) return 0;
      const n = parseInt(item["col" + cat.分类ID], 10);
      return isNaN(n) ? 0 : n;
    },
    // 4 类是否齐全（材料台账进度）
    missingCats(item) {
      if (!this.checkCategories.length || !item) return [];
      return this.checkCategories.filter((c) => this.catServerCount(item, c) <= 0);
    },
    statusDone(item) {
      return this.missingCats(item).length === 0;
    },
    statusText(item) {
      const missing = this.missingCats(item).map((c) => c.分类名称);
      if (missing.length === 0) return "已完成";
      return "进行中，还差 " + missing.join("、");
    },
    async loadCategories() {
      try {
        const res = await getCheckCategories();
        if (res.code === 0) this.checkCategories = res.data || [];
      } catch (e) {
        console.error("加载分类失败:", e);
      }
    },
    async loadRecords() {
      this.loading = true;
      try {
        const res = await getCheckedRecords();
        if (res.code === 0) {
          this.records = res.data || [];
        }
      } catch (error) {
        console.error("加载记录失败:", error);
      } finally {
        this.loading = false;
      }
    },
    releasePreviewUrls() {
      this.previewUrls.forEach((u) => URL.revokeObjectURL(u));
      this.previewUrls = [];
    },
    // 预览某车某分类已传照片
    async previewCategory(item, cat) {
      if (!item || !item.orderNo) {
        Toast("未关联订单，无法查看材料");
        return;
      }
      Toast.loading({ message: "加载中...", forbidClick: true, duration: 0 });
      try {
        const res = await getVehicleMaterials({
          orderNo: item.orderNo,
          vin: item.vid,
          categoryId: cat.分类ID,
        });
        const list = (res && res.code === 0 && res.data) || [];
        if (list.length === 0) {
          Toast.clear();
          Toast("该分类没有材料");
          return;
        }
        const images = [];
        this.releasePreviewUrls();
        for (const m of list) {
          const blob = await getMaterialFileBlob(m.attachmentId);
          if (blob && blob.type && blob.type.startsWith("image/")) {
            const url = URL.createObjectURL(blob);
            this.previewUrls.push(url);
            images.push(url);
          }
        }
        Toast.clear();
        if (images.length === 0) {
          Toast("没有可预览的图片");
          return;
        }
        ImagePreview({ images, startPosition: 0, closeable: true });
      } catch (error) {
        Toast.clear();
        Toast.fail("加载失败");
        console.error("预览失败:", error);
      }
    },
    // 回待盘点（清空盘点标记；材料保留）
    confirmReset(item) {
      Dialog.confirm({
        title: "回待盘点",
        message: "该车将回到待盘点列表（已传材料保留），确定吗？",
        confirmButtonColor: "#ee0a24",
      })
        .then(async () => {
          try {
            const formData = new FormData();
            formData.append("recordId", item.no);
            const res = await deleteCheckRecord(formData);
            if (res.code === 0) {
              Toast.success("已回到待盘点");
              this.loadRecords();
            } else {
              Toast.fail(res.message || "操作失败");
            }
          } catch (error) {
            Toast.fail("操作失败");
          }
        })
        .catch(() => {});
    },
  },
  beforeDestroy() {
    this.releasePreviewUrls();
  },
};
</script>

<style scoped>
.records {
  max-width: 512px;
  margin: 0 auto;
  min-height: 100vh;
  background-color: #f5f5f5;
}

.search-bar {
  padding: 8px 16px;
  background: #fff;
}

.order-list {
  padding: 12px 16px;
}

.empty-tip {
  text-align: center;
  color: #999;
  padding: 40px;
}

.order-card {
  background: #fff;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  cursor: pointer;
}

.order-card:active {
  background: #f8f8f8;
}

.order-head {
  display: flex;
  align-items: center;
}

.order-no {
  flex: 1;
  font-size: 15px;
  font-weight: bold;
  color: #333;
  word-break: break-all;
}

.order-no.unlinked {
  color: #999;
  font-weight: normal;
}

.order-count {
  font-size: 12px;
  color: #07c160;
  margin-left: 8px;
  white-space: nowrap;
}

.order-arrow {
  font-size: 12px;
  color: #999;
  margin-left: 8px;
  white-space: nowrap;
}

.brand-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.brand-chip {
  font-size: 12px;
  color: #576b95;
  background: #eef3fb;
  border-radius: 4px;
  padding: 2px 8px;
}

.order-detail {
  margin-top: 10px;
  border-top: 1px dashed #eee;
  padding-top: 6px;
}

.brand-block {
  margin-bottom: 8px;
}

.brand-title {
  font-size: 13px;
  font-weight: bold;
  color: #444;
  padding: 4px 0;
}

.vin-row {
  display: flex;
  align-items: flex-start;
  background: #fafafa;
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 6px;
}

.vin-main {
  flex: 1;
  min-width: 0;
}

.vin-top {
  display: flex;
  align-items: center;
}

.vin-vid {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: bold;
  color: #333;
  word-break: break-all;
}

.del-link {
  font-size: 11px;
  color: #ff4d4f;
  margin-left: 8px;
  white-space: nowrap;
  flex-shrink: 0;
}

.state-tag {
  font-size: 11px;
  border-radius: 10px;
  padding: 0 6px;
  line-height: 16px;
  margin-left: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 46%;
  flex-shrink: 0;
}

.state-tag.ok {
  color: #07c160;
  background: #e8f9ef;
  border: 1px solid #9fe6bf;
}

.state-tag.doing {
  color: #ff7d00;
  background: #fff5ec;
  border: 1px solid #ffd9a6;
}

.vin-meta {
  font-size: 12px;
  color: #999;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cat-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}

.cat-chip {
  font-size: 11px;
  border-radius: 10px;
  padding: 0 6px;
  line-height: 16px;
}

.cat-chip.todo {
  color: #999;
  background: #f7f7f7;
  border: 1px solid #eee;
}

.cat-chip.done {
  color: #07c160;
  background: #e8f9ef;
  border: 1px solid #9fe6bf;
  font-weight: 600;
}
</style>
